/**
 * Reglas del wizard de Lift: qué opciones se deshabilitan y cuándo se puede avanzar.
 *
 * Es el gemelo de `coherence.ts` de Wio y, como aquel, un SUBCONJUNTO del motor
 * server-side: el perfil oficial lo calcula wio.suitability.api, que además rechaza
 * con un 400 la combinación imposible de Q21×Q22 (`EnsureHorizonAllowsObjective`).
 * Aquí sólo se bloquea el avance y se le explica al cliente por qué.
 *
 * Tener la regla en TS y en C# es duplicación consciente (ADR-0002 §3 decisión 14):
 * unificarla queda como mejora futura.
 */
import {
  BLOCK1_QUESTIONS,
  BLOCK2_QUESTIONS,
  BLOCK3_QUESTIONS,
  type LiftAnswers,
  type ScoredQuestionKey,
} from './answers';

/** Q21: órdenes 1 (menos de 1 año) y 2 (entre 1 y 3 años) son horizonte corto. */
const SHORT_HORIZON_MAX_ORDER = 2;

/** Q22: órdenes 3 (aumento medio) y 4 (crecimiento alto) son objetivo de riesgo. */
export const RISKY_OBJECTIVE_MIN_ORDER = 3;

/** Clave i18n del motivo que se muestra en las opciones de Q22 que el horizonte veta. */
export const RISKY_OBJECTIVE_DISABLED_REASON_KEY =
  'lift.disabled.horizonRulesOutObjective';

export const isShortHorizon = (q21?: number): boolean =>
  q21 !== undefined && q21 <= SHORT_HORIZON_MAX_ORDER;

const isRiskyObjective = (q22?: number): boolean =>
  q22 !== undefined && q22 >= RISKY_OBJECTIVE_MIN_ORDER;

/** Con horizonte de 3 años o menos, la finalidad no puede ser de riesgo medio o alto. */
export const isObjectiveBlocked = (q21?: number, q22?: number): boolean =>
  isShortHorizon(q21) && isRiskyObjective(q22);

/**
 * Qué órdenes de Q22 hay que deshabilitar dado el horizonte elegido. La regla vive aquí
 * y no en el paso, igual que `getIncompatibleP9Options` en Wio: si estuviera en el
 * componente habría dos sitios donde leer el mismo umbral.
 */
export const getBlockedObjectiveOrders = (
  q21: number | undefined,
  orders: number[],
): number[] => orders.filter((order) => isObjectiveBlocked(q21, order));

const allAnswered =
  (questions: readonly ScoredQuestionKey[]) =>
  (answers: LiftAnswers): boolean =>
    questions.every((question) => answers[question] !== undefined);

export const isBlock1Complete = allAnswered(BLOCK1_QUESTIONS);

export const isBlock2Complete = allAnswered(BLOCK2_QUESTIONS);

/**
 * El bloque de objetivos exige además que la combinación Q21×Q22 sea posible. Con las
 * opciones deshabilitadas y la limpieza automática no debería llegar a darse, pero
 * esta puerta es lo único que impide mandarle al API un test que va a rechazar.
 */
export const isBlock3Complete = (answers: LiftAnswers): boolean =>
  allAnswered(BLOCK3_QUESTIONS)(answers) &&
  !isObjectiveBlocked(answers.q21, answers.q22);
